import { useEffect, useState } from 'react'

import { Box, Container } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import {
	guardUnsavedAction,
	useUnsavedIdeationWarning,
} from 'utils/useUnsavedIdeationWarning'

import { RoundRobinModel } from 'models/RoundRobinModel'
import { Game } from 'models/gameModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import {
	postRoundRobinPrompt,
	postRoundRobinResponse,
} from 'services/completions.service'
import { postIdea } from 'services/ideas.service'

import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Hero from '../../components/Hero/Hero'
import { styles } from './RoundRobin.styles'
import Section1 from './components/Section1/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'

export const TEST_ID = 'round-robin'

const userMessage = {
	content: '',
	role: 'user',
}

const RoundRobin = ({ game }: { game?: Game }) => {
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)

	const {
		authorIdeation,
		reset,
		resetSoft,
		setAuthorIdeation,
		companyName,
		companyProblem,
		generatePromptsDisabled,
		setCompanyName,
		setCompanyProblem,
		ValidGameScenario,
		promptToJSON,
		setCurrentPrompts,
		updatePromptConversationHistory,
		postObject,
		currentResponse,
		currentPrompts,
		responseConversation,
		submitIdeasDisabled,
		responseToJSON,
		setResponseConversation,
		setCurrentResponse,
		setGameId,
		termsAgreed,
		setTermsAgreed,
		roundRobinPrompt,
		setPromptResponse,
	} = RoundRobinModel()

	useEffect(() => {
		if (game) {
			setGameId(game.gameId)
			setCompanyName(game.innovationCompany)
			setCompanyProblem(game.companyProblem)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const { showModal } = useModalContext()
	const [isAiLoading, setIsAiLoading] = useState(false)
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const isUnsaved = hasUnsavedIdeation(step, authorIdeation)

	useUnsavedIdeationWarning(isUnsaved, authorIdeation, showModal)

	useEffect(() => {
		handleAiResponseClick()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPrompts, setCurrentPrompts])

	const { setIdeaStreakCount } = useIdeaStreakContext()
	const { showAlert } = useToastContext()

	const updateAIResponse = (content: string) => {
		return {
			content,
			role: 'assistant',
		}
	}
	const resetAllField = () => {
		setCompanyProblem('')
		setCompanyName('')
		setCompanyProblem('')
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const refreshPrompt = async () => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				if (refreshClicksSinceIdeation < 2) {
					await handlePrompts()
				}

				setRefreshClicksSinceIdeation((prev) => prev + 1)
			},
		})
	}

	const handlePrompts = async () => {
		try {
			resetSoft()
			setIsAiLoading(true)
			setStep(GAME_STEP.Step2)
			// need to get the conversation for each game
			const [
				factFinderResponse,
				opportunityExplorerPrompt,
				challengeIdentifyResponse,
				testingStrategistPrompt,
			] = await Promise.all([
				postRoundRobinPrompt(promptToJSON(0), ValidGameScenario[0]),
				postRoundRobinPrompt(promptToJSON(1), ValidGameScenario[1]),
				postRoundRobinPrompt(promptToJSON(2), ValidGameScenario[2]),
				postRoundRobinPrompt(promptToJSON(3), ValidGameScenario[3]),
			])

			// make sure everything is ok
			if (
				factFinderResponse.status === 200 &&
				opportunityExplorerPrompt.status === 200 &&
				challengeIdentifyResponse.status === 200 &&
				testingStrategistPrompt.status === 200
			) {
				// set the current prompts
				setCurrentPrompts([
					factFinderResponse.data,
					challengeIdentifyResponse.data,
					testingStrategistPrompt.data,
					opportunityExplorerPrompt.data,
				])

				setPromptResponse({
					data: {
						ideas: [
							factFinderResponse.data,
							challengeIdentifyResponse.data,
							testingStrategistPrompt.data,
							opportunityExplorerPrompt.data,
						],
					},
					status: testingStrategistPrompt.status,
				})

				// update history
				updatePromptConversationHistory(0, factFinderResponse.data)
				updatePromptConversationHistory(
					1,
					opportunityExplorerPrompt.data
				)
				updatePromptConversationHistory(
					2,
					challengeIdentifyResponse.data
				)
				updatePromptConversationHistory(3, testingStrategistPrompt.data)

				await handleAiResponseClick()
			} else {
				throw Error('Not all responses received successfully')
			}
		} catch (error) {
			if (showAlert) {
				showAlert(defaultErrorMessage)
			}
		} finally {
			setIsAiLoading(false)
		}
	}

	const handleManualPromptChange = async (newValues: string[]) => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				try {
					resetSoft()

					setCurrentPrompts([
						newValues[0],
						newValues[1],
						newValues[2],
						newValues[3],
					])

					updatePromptConversationHistory(0, newValues[0])
					updatePromptConversationHistory(1, newValues[1])
					updatePromptConversationHistory(2, newValues[2])
					updatePromptConversationHistory(3, newValues[3])

					await handleAiResponseClick()
				} catch (error) {
					if (showAlert) {
						showAlert(defaultErrorMessage)
					}
				} finally {
					setIsAiLoading(false)
				}
			},
		})
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(postObject())
			if (response.status !== 200) {
				throw new Error(
					`Failed to submit idea. Status: ${response.status}`
				)
			}

			showAlert?.({
				severity: ToastSeverity.SUCCESS,
			})

			setIdeaStreakCount((prevState) => prevState + 1)
			// need to reset the model
			reset()

			window.scrollTo({
				top: 0,
			})
			setStep(GAME_STEP.Step1) // TODO: Do we really want to go back to step 1 here?
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.submitIdeaError.title,
					bodyText: alertTitleTextMap.submitIdeaError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	const handleAiResponseClick = async () => {
		const prompt = responseToJSON()
		prompt.conversation = [...responseConversation, userMessage]

		try {
			const response = await postRoundRobinResponse(prompt)

			if (response.status === 200 && response.data) {
				const msg = response.data.ideas.join('\n')

				if (msg) {
					// add response to state
					setResponseConversation((prevMessages) => [
						...prevMessages,
						userMessage,
						updateAIResponse(msg),
					])

					setCurrentResponse(msg)
				} else {
					if (showAlert) {
						showAlert(defaultErrorMessage)
					}
				}
			} else {
				throw Error(`response received ${response}`)
			}
		} catch (error) {
			if (showAlert) {
				showAlert(defaultErrorMessage)
			}
		} finally {
			setIsAiLoading(false)
		}
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	useEffect(() => {
		setRefreshClicksSinceIdeation(0)
	}, [authorIdeation])

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				data-testid="innovation-modal"
				step={step}
				gameTypeEnum={GAME_TYPE.ROUND_ROBIN}
				innovationCompany={companyName}
				companyProblem={companyProblem}
				setInnovationCompany={setCompanyName}
				setCompanyProblem={setCompanyProblem}
				isLoading={isAiLoading}
				createPrompt={handlePrompts}
				hideEdit={Boolean(game && (game.gameTitle || game.endTime))}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth="md"
				sx={styles.container}
			>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isAiLoading}
						companyName={companyName}
						setCompanyName={setCompanyName}
						companyIdea={companyProblem}
						setCompanyIdea={setCompanyProblem}
						generatePromptsDisabled={generatePromptsDisabled}
						resetAllField={resetAllField}
						createPrompt={handlePrompts}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
						navigateTo={(path: string) =>
							window.open(
								`${window.location.origin}${path}`,
								'_blank'
							)
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Section2
							loading={isAiLoading || isSubmitButtonLoading}
							currentResponse={currentResponse}
							currentPrompts={currentPrompts}
							createPrompt={refreshPrompt}
							changePrompts={handleManualPromptChange}
							roundRobinPrompt={roundRobinPrompt}
						/>
						<Section3
							submitIdeasDisabled={submitIdeasDisabled}
							loading={isAiLoading || isSubmitButtonLoading}
							submitIdeas={handleSubmitIdeas}
							authorIdeation={authorIdeation}
							setAuthorIdeation={setAuthorIdeation}
							viewSubmittedIdeas={viewSubmittedIdeas}
							cautionText={
								game?.gameTitle || game?.endTime
									? brainstormText.global.alternateWarningText
									: brainstormText.global.warningText
							}
							termsAgreed={termsAgreed}
							setTermsAgreed={setTermsAgreed}
							gameId={game?.gameId}
						/>
					</>
				)}
			</Container>
		</Box>
	)
}

export default RoundRobin
